import type { SelectItem, SelectItemProps } from '@mantine/core';
import { createStyles, Group, Stack, Tooltip } from '@mantine/core';
import {
	Button,
	MultiSelect,
	Select,
	Switch,
	Text,
	TextArea,
} from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { forwardRef, useMemo } from 'react';
import type { IApiListResponse, ISecodaEntity } from '../../../api';
import { SecodaEntityIcon } from '../../../components/SecodaEntity/SecodaEntityIcon';
import type { AiOutputBlockStore, AiSettingStore } from '../store';
import { isAiBindDisabled } from '../utils';

const useStyles = createStyles((theme) => ({
	stack: {
		padding: theme.spacing.xl,
		gap: theme.spacing.md,
	},
	innerStack: {
		gap: theme.spacing['3xs'],
	},
	group: {
		padding: 0,
	},
}));

interface SelectItemComponentProps extends SelectItemProps {
	disabled?: boolean;
}

interface AiSettingModalLeftColumnContentProps {
	store: AiOutputBlockStore;
	aiSettingStore?: AiSettingStore;
	onPreviewOnClick: () => void;
	resources: IApiListResponse<ISecodaEntity> | undefined;
}

export const AiSettingModalLeftColumnContent = observer(
	({
		store,
		aiSettingStore,
		onPreviewOnClick,
		resources,
	}: AiSettingModalLeftColumnContentProps) => {
		const { classes } = useStyles();

		const handlePreviewOnClick = () => {
			aiSettingStore?.resetErrors();

			if (aiSettingStore?.checkIsPreviewReady()) {
				onPreviewOnClick();
			}
		};

		const data: SelectItem[] = useMemo(
			() =>
				store.aiSettingTypes.map((aiSettingType) => ({
					...aiSettingType,
					disabled: isAiBindDisabled(
						aiSettingType.value,
						store.aiSettings,
						aiSettingStore?.aiSettingAction.id ?? ''
					),
				})),
			[
				store.aiSettingTypes,
				aiSettingStore?.aiSettingAction.id,
				store.aiSettings,
			]
		);

		const SelectItemComponent = forwardRef<
			HTMLDivElement,
			SelectItemComponentProps
		>(({ label, disabled, ...others }: SelectItemComponentProps, ref) => (
			<Group ref={ref} {...others}>
				{disabled && (
					<Tooltip label="Currently used in another AI output">
						<Text size="sm" color="text/primary/disabled">
							{label}
						</Text>
					</Tooltip>
				)}
				{!disabled && <Text size="sm">{label}</Text>}
			</Group>
		));

		SelectItemComponent.displayName = 'SelectItemComponent';

		return (
			<Stack className={classes.stack}>
				<Select
					label="Type"
					data={data}
					placeholder="Select type"
					value={aiSettingStore?.aiSettingAction.type}
					itemComponent={SelectItemComponent}
					onChange={(value) => {
						// Foundations Select may pass a string or an option object
						const coerced =
							typeof value === 'string'
								? value
								: (value as unknown as { value?: string } | null)?.value;
						aiSettingStore?.setType(coerced ?? undefined);
					}}
					error={
						aiSettingStore?.displayTypeError
							? 'This field is required'
							: undefined
					}
				/>
				<TextArea
					label="Prompt"
					placeholder="Enter prompt"
					value={aiSettingStore?.aiSettingAction.prompt}
					autosize
					maxRows={12}
					minRows={12}
					mih={280}
					onChange={(event) =>
						aiSettingStore?.setPrompt(event.currentTarget.value)
					}
					error={
						aiSettingStore?.displayPromptError
							? 'This field is required'
							: undefined
					}
					description={
						aiSettingStore?.aiSettingAction.type !== 'custom' &&
						!!aiSettingStore?.aiSettingAction.type
							? 'Template prompt pre-filled. You can edit this as needed.'
							: undefined
					}
				/>
				{aiSettingStore?.aiSettingAction.type === 'custom' && (
					<Stack className={classes.innerStack}>
						<Text size="sm" weight="semibold">
							Select fields to update
						</Text>
						<Switch
							label="Description"
							checked={!!aiSettingStore?.aiSettingAction.bind_description}
							onChange={(event) =>
								aiSettingStore?.setBindDescription(event.currentTarget.checked)
							}
							disabled={isAiBindDisabled(
								'description',
								store.aiSettings,
								aiSettingStore?.aiSettingAction.id
							)}
						/>
						<Switch
							label="Documentation"
							checked={!!aiSettingStore?.aiSettingAction.bind_definition}
							onChange={(event) =>
								aiSettingStore?.setBindDefinition(event.currentTarget.checked)
							}
							disabled={isAiBindDisabled(
								'documentation',
								store.aiSettings,
								aiSettingStore?.aiSettingAction.id
							)}
						/>
						<Switch
							label="Tags"
							checked={!!aiSettingStore?.aiSettingAction.bind_tags}
							onChange={(event) =>
								aiSettingStore?.setBindTags(event.currentTarget.checked)
							}
							disabled={isAiBindDisabled(
								'tags',
								store.aiSettings,
								aiSettingStore?.aiSettingAction.id
							)}
						/>
						<Switch
							label="PII"
							checked={!!aiSettingStore?.aiSettingAction.bind_pii}
							onChange={(event) =>
								aiSettingStore?.setBindPii(event.currentTarget.checked)
							}
							disabled={isAiBindDisabled(
								'pii',
								store.aiSettings,
								aiSettingStore?.aiSettingAction.id
							)}
						/>
						{aiSettingStore?.displayBindError && (
							<Text size="sm" color="text/critical/default">
								Select at least one field to bind
							</Text>
						)}
					</Stack>
				)}
				<Stack className={classes.innerStack}>
					<MultiSelect
						label="Select resources to preview"
						data={
							resources?.results.map((resource) => ({
								label: resource.title,
								value: resource.id,
								icon: <SecodaEntityIcon entity={resource} size={20} />,
							})) ?? []
						}
						placeholder="Select resources to preview"
						value={aiSettingStore?.aiSettingAction.resources_to_preview ?? []}
						setValue={(value) =>
							aiSettingStore?.setResourcesToPreview(value ?? [])
						}
						error={
							aiSettingStore?.displayResourcesToPreviewError
								? 'This field is required'
								: undefined
						}
						searchable
						displayEntity
						searchValue={store.searchTerm}
						onSearchChange={(value) => {
							store.setSearchTerm(value);
						}}
						renderIcon={(item) => item.icon}
						maxSelectedValues={3}
					/>
					<Text size="sm" variant="tertiary" color="text/secondary/default">
						Choose up to 3 resources to validate this output. A preview will be
						generated based on your selection.
					</Text>
				</Stack>
				<Group spacing="sm" position="right" className={classes.group}>
					<Button
						variant="primary"
						onClick={handlePreviewOnClick}
						loading={store.isPreviewLoading}
						disabled={store.isPreviewLoading}
					>
						Generate preview
					</Button>
				</Group>
			</Stack>
		);
	}
);
